import assignmentService from 'ApiService/Teacher/Assignment';
import testService from 'ApiService/Teacher/TestService';
import InputField from 'components/fields/InputField';
import { AuthContext } from 'contexts/AuthContext';
import Nodatafound from 'pages/Common/Nodatafound';
import { formatDate } from 'pages/helpers/dateUtils';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';

const ViewTeacherTests = () => {
  const [createdTests, setCreatedTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [teacherScores, setTeacherScores] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterScheduleType, setFilterScheduleType] = useState('');
  const [filterScheduledDate, setFilterScheduledDate] = useState('');
  const [filterCreatedDate, setFilterCreatedDate] = useState('');
  const [filteredTests, setFilteredTests] = useState([]);
  const [filterTestType, setFilterTestType] = useState('');
  const { state } = useContext(AuthContext);
  const resetFilters = () => {
    setFilterScheduleType('');
    setFilterTestType('');
    setFilterScheduledDate('');
    setFilterCreatedDate('');
  };
  const fetchCreatedTests = async () => {
    setLoading(true);
    try {
      const response = await assignmentService.getCreatedAssignments();
      if (response.data.success) {
        setCreatedTests(response.data.data);
        setFilteredTests(response.data.data);
      } else {
        toast.error(
          response.data.message || 'Failed to fetch created assignment.'
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch created assignment.'
      );
    } finally {
      setLoading(false);
    }
  };
  const applyFilters = () => {
    let filtered = [...createdTests];

    if (filterScheduleType) {
      filtered = filtered.filter(
        (test) =>
          test.assignmentSchedule?.toLowerCase() ===
          filterScheduleType.toLowerCase()
      );
    }

    if (filterTestType) {
      filtered = filtered.filter(
        (test) => test.type?.toLowerCase() === filterTestType.toLowerCase()
      );
    }

    if (filterScheduledDate) {
      filtered = filtered.filter(
        (test) =>
          test.scheduledAt &&
          new Date(test.scheduledAt).toLocaleDateString() ===
            new Date(filterScheduledDate).toLocaleDateString()
      );
    }
    if (filterCreatedDate) {
      filtered = filtered.filter(
        (test) =>
          test.createdAt &&
          new Date(test.createdAt).toLocaleDateString() ===
            new Date(filterCreatedDate).toLocaleDateString()
      );
    }

    setFilteredTests(filtered);
  };
  useEffect(() => {
    applyFilters();
  }, [
    filterScheduleType,
    filterScheduledDate,
    filterCreatedDate,
    filterTestType,
    createdTests,
  ]);
  // Fetch submissions for a specific test
  const fetchSubmissions = async (testId) => {
    setLoading(true);
    try {
      const response = await assignmentService.getSubmissionsByAssignmentId(
        testId
      );
      if (response.data.success) {
        setSubmissions(response.data.data);
        setSelectedTest(createdTests.find((test) => test._id === testId));
      } else {
        toast.error(response.data.message || 'Failed to fetch submissions.');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch submissions.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle assigning scores to QA questions
  const handleScoreChange = (questionId, value) => {
    setTeacherScores((prevScores) => ({
      ...prevScores,
      [questionId]: value,
    }));
  };

  // Submit scores for a QA test
  const handleSubmitScores = async () => {
    try {
      // Calculate total score
      const totalScore = selectedSubmission.questions.reduce(
        (sum, question) => {
          const score = parseFloat(teacherScores[question.questionId]) || 0; // Get score for each question
          return sum + score;
        },
        0
      );

      console.log('Submitting scores:', {
        submissionId: selectedSubmission.submissionId,
        totalScore,
      });
      const data = {
        submissionId: selectedSubmission.submissionId,
        totalScore,
      };

      const response = await testService.submitAssignmentQAsScores(data);

      if (response.data.success) {
        toast.success('Scores submitted successfully!');
        setSelectedSubmission(null);
      } else {
        toast.error(response.data.message || 'Failed to submit scores.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to submit scores.');
    }
  };

  useEffect(() => {
    if (state?.user) {
      fetchCreatedTests();
    }
  }, [state?.user]);

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <BallTriangle color="#5431ed" />
      </div>
    );
  }

  return (
    <div className="mt-4 h-full w-full rounded-[20px] md:mt-5">
      {/* Filters */}
      <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center">
        <div className="w-full md:w-1/5">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Schedule Type
          </label>
          <select
            value={filterScheduleType}
            onChange={(e) => setFilterScheduleType(e.target.value)}
            className="mt-2 w-full cursor-pointer rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">All</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="end assignment">End Assignment</option>
          </select>
        </div>
        <div className="w-full md:w-1/5">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Assignment Type
          </label>
          <select
            value={filterTestType}
            onChange={(e) => setFilterTestType(e.target.value)}
            className="mt-2 w-full cursor-pointer rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">All</option>
            <option value="mcq">MCQ</option>
            <option value="qa">QA</option>
          </select>
        </div>
        <div className="flex flex-col gap-2">
          <InputField
            label="Scheduled Date"
            type="date"
            value={filterScheduledDate}
            onChange={(e) => setFilterScheduledDate(e.target.value)}
            className="rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-navy-800 dark:text-gray-300 sm:text-sm"
          />
        </div>
        <div className="flex flex-col gap-2">
          <InputField
            label="Created Date"
            type="date"
            value={filterCreatedDate}
            onChange={(e) => setFilterCreatedDate(e.target.value)}
            className="rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-navy-800 dark:text-gray-300 sm:text-sm"
          />
        </div>

        <div className=" mt-8 flex w-full items-end justify-end md:w-1/5 ">
          <button
            onClick={resetFilters}
            className="linear cursor-pointer rounded-xl bg-brand-900 px-8 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
          >
            Reset Filters
          </button>
        </div>
      </div>
      {selectedSubmission ? (
        // Display selected submission details
        <div className="grid h-full w-full grid-cols-1 gap-4 rounded-lg border p-2 text-navy-800 dark:text-white">
          <h3 className="text-2xl font-bold">
            {selectedSubmission.student.name}'s Submission
          </h3>
          <p>Assignment Title: {selectedSubmission.title}</p>
          <p>Score: {selectedSubmission.score}</p>

          {selectedSubmission.questions.map((question, index) => (
            <div
              key={index}
              className="rounded-lg bg-white p-4 shadow-md dark:bg-navy-900"
            >
              <h5 className="text-lg font-bold text-gray-900 dark:text-white">
                Question {index + 1}: {question.questionText}
              </h5>
              <p className="mt-2 text-green-600 dark:text-green-400">
                Correct Answer: {question.correctAnswer}
              </p>
              <p className="mt-2">
                Student Answer:{' '}
                <span
                  className={
                    question.isCorrect
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }
                >
                  {question.studentAnswer || 'Not answered'}
                </span>
              </p>
              {selectedSubmission.type === 'qa' && (
                <div className="mt-4">
                  {selectedSubmission.score === 0 ? (
                    <>
                      <label className="block text-sm font-medium text-gray-700">
                        Teacher Score:
                      </label>
                      <input
                        type="number"
                        value={teacherScores[question.questionId] || ''}
                        onChange={(e) =>
                          handleScoreChange(question.questionId, e.target.value)
                        }
                        className="block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-navy-800 dark:text-gray-300 sm:text-sm"
                      />
                    </>
                  ) : (
                    <p className="text-gray-700 dark:text-gray-300">
                      {teacherScores[question.questionId]}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
          <div className="mt-4 flex flex-col justify-between px-3 pt-2 md:flex-row md:px-8">
            {selectedSubmission.type === 'qa' && (
              <button
                onClick={handleSubmitScores}
                disabled={selectedSubmission.score !== 0}
                className="linear mt-4 cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-300"
              >
                {selectedSubmission.score !== 0
                  ? 'Score Already Submitted'
                  : 'Submit Scores'}
              </button>
            )}

            <button
              onClick={() => setSelectedSubmission(null)}
              className="mt-4 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white"
            >
              Back
            </button>
          </div>
        </div>
      ) : selectedTest ? (
        <div className="grid h-full w-full grid-cols-1 gap-4 p-2 md:grid-cols-2 lg:grid-cols-3">
          {submissions.length > 0 ? (
            <>
              {submissions.map((submission) => (
                <span
                  onClick={() => setSelectedSubmission(submission)}
                  key={submission.submissionId}
                  className="cursor-pointer rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none hover:border-brand-500 dark:border-[#323B5D] dark:!bg-navy-900 dark:bg-navy-900 dark:text-white"
                >
                  <div className="justify flex h-full w-full flex-col p-4 leading-normal md:w-2/3">
                    <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                      {submission.student.name}
                    </h5>
                    <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                      Score: {submission.score || 'n/a'}
                    </p>
                    <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                      Submitted:{' '}
                      {new Date(submission.submittedAt).toLocaleDateString()}
                    </p>
                  </div>
                </span>
              ))}

              <div className="col-span-full mt-4 flex flex-col justify-end px-3 pt-2 md:flex-row md:px-8">
                <button
                  onClick={() => setSelectedTest(null)}
                  className="linear cursor-pointer rounded-xl bg-brand-900 bg-lightPrimary px-16 px-16 py-2 py-2 font-medium font-medium text-navy-700 text-white transition duration-200 dark:bg-white/5 dark:text-white"
                >
                  Back
                </button>
              </div>
            </>
          ) : (
            <div className="col-span-full flex flex-col items-center">
              <Nodatafound />
              <button
                onClick={() => setSelectedTest(null)}
                className="mt-4 w-auto cursor-pointer rounded-xl bg-lightPrimary px-4 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white"
              >
                Back
              </button>
            </div>
          )}
        </div>
      ) : (
        // Display list of created tests
        <div className="grid h-full max-h-[68vh] w-full grid-cols-1  gap-4 overflow-hidden overflow-y-scroll rounded-xl border border-gray-200 p-2 dark:border-white/10 md:grid-cols-2 lg:grid-cols-3">
          {filteredTests.length > 0 ? (
            filteredTests.map((test) => (
              <span
                onClick={() => fetchSubmissions(test._id)}
                key={test._id}
                className="cursor-pointer rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none hover:border-brand-500 dark:border-[#323B5D] dark:!bg-navy-900 dark:bg-navy-900 dark:text-white"
              >
                <div className="justify flex h-full w-full flex-col p-4 leading-normal md:w-2/3">
                  <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                    {test.title}
                  </h5>
                  <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                    Type: {test.type.toUpperCase()}
                  </p>
                  <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                    Created Date: {formatDate(test.createdAt)}
                  </p>
                  <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                    Scheduled Date: {formatDate(test.scheduledAt) || 'N/A'}
                  </p>
                  <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                    Assignment Schedule:{' '}
                    {test.assignmentSchedule || 'Not Scheduled Yet'}
                  </p>
                </div>
              </span>
            ))
          ) : (
            <div className="col-span-full">
              <Nodatafound title="NO DATA FOUND" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewTeacherTests;
